import { create } from '@thoughtspot/logger';
import noop from 'lodash/noop';
import React, { useEffect, useState } from 'react';

declare const window: any;

// Constants
const SESSION_INFO_UPDATED_TYPE = 'session-info-updated';

// Types
interface UpdateServiceOptions {
    preauthInfoUpdated?: boolean;
}

interface SessionAwareAppRootProps {
    children: React.ReactNode;
    onSessionUpdate?: (data: any, options?: UpdateServiceOptions) => Promise<void>;
}

const logger = create('SessionAwareAppRoot');

export const SessionAwareAppRoot: React.FC<SessionAwareAppRootProps> = ({ 
    children, 
    onSessionUpdate 
}) => {
    const [appIndex, setAppIndex] = useState(0);

    const forceFullRerender = () => {
        setAppIndex(prev => prev + 1);
    };

    const handleSessionInfoUpdate = async () => {
        try {
            if (onSessionUpdate) {
                await onSessionUpdate(
                    false,
                    { preauthInfoUpdated: true },
                );
            }
            
            // Force full re-render to ensure all components pick up the updated 
            // configuration, styles, feature flags, and authentication state
            logger.log('[SW] triggering force rerender');
            forceFullRerender();
        } catch (error) {
            // Error parsing response meta - silently handled
        }
    };

    useEffect(() => {
        if (window.blink?.swUpdatedInfo?.isUpdated) {
            window.blink.swUpdatedInfo.isUpdated = false;
            handleSessionInfoUpdate();
        } else if (navigator.serviceWorker) {
            const handleServiceWorkerMessage = async (event: any) => {
                if (event?.data?.type === SESSION_INFO_UPDATED_TYPE) {
                    handleSessionInfoUpdate();
                }
            };

            navigator.serviceWorker.addEventListener(
                'message',
                handleServiceWorkerMessage,
            );

            // Cleanup function
            return () => {
                navigator.serviceWorker.removeEventListener(
                    'message',
                    handleServiceWorkerMessage,
                );
            };
        }
        return noop;
    }, []);

    return (
        <div key={`app-root-${appIndex}`} style={{ display: 'contents' }}>
            {children}
        </div>
    );
};
